/**
 * The score. Seven acts, each one a formation plus everything that dresses it:
 * where the camera sits, how the simulation behaves, and how the frame is graded.
 *
 * Scroll position maps to a continuous act index, and every value below is
 * interpolated between neighbouring acts — so there are no cuts anywhere in the
 * piece, only crossfades.
 */

const srgbToLinear = (c) =>
  c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

/** Hex to linear-light rgb. Everything downstream works in linear. */
export function rgb(hex) {
  const n = parseInt(hex.replace("#", ""), 16);
  return [
    srgbToLinear(((n >> 16) & 255) / 255),
    srgbToLinear(((n >> 8) & 255) / 255),
    srgbToLinear((n & 255) / 255),
  ];
}

export const ACTS = [
  {
    id: "genesis",
    formation: "genesis",
    kicker: "00 / ignition",
    camera: { pos: [0, 24, 142], look: [0, 0, 0], fov: 55 },
    sim: { attract: 1.1, damp: 0.955, turb: 5.2, noiseScale: 0.011, swirl: 0.16 },
    look: {
      size: 1.5,
      intensity: 1.7,
      iridescence: 0.15,
      cold: rgb("#b36c33"),
      warm: rgb("#ff8a3c"),
      hot: rgb("#fff2d0"),
    },
    sky: {
      top: rgb("#0a0710"),
      bottom: rgb("#02020a"),
      nebulaColor: rgb("#2a1408"),
      nebulaAmount: 0.5,
      stars: 1.0,
      oil: 0.0,
    },
    grade: { bloom: 1.35, aberration: 0.9, exposure: 1.18, trail: 0.9, vignette: 0.85 },
  },
  {
    id: "identity",
    formation: "identity",
    kicker: "01 / who",
    camera: { pos: [0, 0, 96], look: [0, 0, 0], fov: 48 },
    sim: { attract: 7.2, damp: 0.895, turb: 0.9, noiseScale: 0.05, swirl: 0.0 },
    look: {
      size: 0.98,
      intensity: 2.1,
      iridescence: 0.06,
      cold: rgb("#c6d4ea"),
      warm: rgb("#e8f0ff"),
      hot: rgb("#ffffff"),
    },
    sky: {
      top: rgb("#070b16"),
      bottom: rgb("#010206"),
      nebulaColor: rgb("#0d1830"),
      nebulaAmount: 0.55,
      stars: 0.85,
      oil: 0.0,
    },
    grade: { bloom: 0.95, aberration: 0.35, exposure: 1.1, trail: 0.62, vignette: 0.8 },
  },
  {
    id: "currently",
    formation: "machine",
    kicker: "02 / currently",
    camera: { pos: [42, 26, 66], look: [0, 0, 0], fov: 52 },
    sim: { attract: 4.2, damp: 0.915, turb: 2.6, noiseScale: 0.035, swirl: 0.22 },
    look: {
      size: 1.25,
      intensity: 1.4,
      iridescence: 0.28,
      cold: rgb("#2f9aa0"),
      warm: rgb("#5ff0dd"),
      hot: rgb("#d9fffb"),
    },
    sky: {
      top: rgb("#04121a"),
      bottom: rgb("#01060a"),
      nebulaColor: rgb("#06333c"),
      nebulaAmount: 0.6,
      stars: 0.6,
      oil: 0.05,
    },
    grade: { bloom: 1.25, aberration: 0.7, exposure: 1.12, trail: 0.8, vignette: 0.78 },
  },
  {
    id: "past",
    formation: "corridor",
    kicker: "03 / past life",
    camera: { pos: [0, 0, 8], look: [0, 0, -150], fov: 80 },
    sim: { attract: 3.4, damp: 0.925, turb: 2.2, noiseScale: 0.028, swirl: 0.1 },
    look: {
      size: 1.15,
      intensity: 1.35,
      iridescence: 0.18,
      cold: rgb("#9a6a24"),
      warm: rgb("#ffb547"),
      hot: rgb("#fff4dd"),
    },
    sky: {
      top: rgb("#100a04"),
      bottom: rgb("#040202"),
      nebulaColor: rgb("#2e1a06"),
      nebulaAmount: 0.45,
      stars: 0.35,
      oil: 0.0,
    },
    grade: { bloom: 1.4, aberration: 1.35, exposure: 1.14, trail: 0.88, vignette: 0.95 },
  },
  {
    id: "projects",
    formation: "constellation",
    kicker: "04 / work",
    camera: { pos: [0, 78, 108], look: [0, 12, 0], fov: 46 },
    sim: { attract: 5.0, damp: 0.905, turb: 1.9, noiseScale: 0.04, swirl: 0.28 },
    look: {
      size: 1.2,
      intensity: 1.4,
      iridescence: 0.35,
      cold: rgb("#7b57b8"),
      warm: rgb("#c79cff"),
      hot: rgb("#ffe6ff"),
    },
    sky: {
      top: rgb("#0b0618"),
      bottom: rgb("#020108"),
      nebulaColor: rgb("#22103f"),
      nebulaAmount: 0.7,
      stars: 0.9,
      oil: 0.08,
    },
    grade: { bloom: 1.2, aberration: 0.6, exposure: 1.1, trail: 0.76, vignette: 0.8 },
  },
  {
    id: "skills",
    formation: "oil",
    kicker: "05 / toolkit",
    camera: { pos: [0, 22, 92], look: [0, -12, 0], fov: 54 },
    sim: { attract: 3.0, damp: 0.94, turb: 3.4, noiseScale: 0.018, swirl: 0.06 },
    look: {
      size: 1.35,
      intensity: 1.3,
      iridescence: 0.95,
      cold: rgb("#4a6f96"),
      warm: rgb("#8fdcff"),
      hot: rgb("#ffd9f5"),
    },
    sky: {
      top: rgb("#050a14"),
      bottom: rgb("#01030a"),
      nebulaColor: rgb("#0a1a2e"),
      nebulaAmount: 0.32,
      stars: 0.45,
      oil: 0.22,
    },
    grade: { bloom: 1.15, aberration: 0.85, exposure: 1.06, trail: 0.82, vignette: 0.86 },
  },
  {
    id: "contact",
    formation: "signal",
    kicker: "06 / signal",
    camera: { pos: [0, 0, 86], look: [0, 0, 0], fov: 46 },
    sim: { attract: 7.0, damp: 0.895, turb: 0.9, noiseScale: 0.05, swirl: 0.04 },
    look: {
      size: 0.98,
      intensity: 2.05,
      iridescence: 0.06,
      cold: rgb("#7fc9dc"),
      warm: rgb("#a6f0ff"),
      hot: rgb("#ffffff"),
    },
    sky: {
      top: rgb("#050d14"),
      bottom: rgb("#010306"),
      nebulaColor: rgb("#0a2230"),
      nebulaAmount: 0.5,
      stars: 1.0,
      oil: 0.1,
    },
    grade: { bloom: 0.95, aberration: 0.35, exposure: 1.12, trail: 0.62, vignette: 0.75 },
  },
];

export const FORMATION_ORDER = ACTS.map((a) => a.formation);

/* --------------------------------------------------------- interpolation --- */

const lerp = (a, b, t) => a + (b - a) * t;

function lerpTriple(a, b, t, out) {
  out[0] = lerp(a[0], b[0], t);
  out[1] = lerp(a[1], b[1], t);
  out[2] = lerp(a[2], b[2], t);
  return out;
}

const scratch = {
  camPos: [0, 0, 0],
  camLook: [0, 0, 0],
  cold: [0, 0, 0],
  warm: [0, 0, 0],
  hot: [0, 0, 0],
  skyTop: [0, 0, 0],
  skyBottom: [0, 0, 0],
  nebula: [0, 0, 0],
};

/**
 * Reads the whole score at a fractional act index and returns one blended
 * state. Reuses scratch arrays — the caller must consume the result before the
 * next call, which the render loop does.
 */
export function sampleActs(index) {
  const clamped = Math.max(0, Math.min(ACTS.length - 1, index));
  const i = Math.min(Math.floor(clamped), ACTS.length - 2);
  const raw = ACTS.length === 1 ? 0 : clamped - i;
  const t = raw * raw * (3 - 2 * raw); // smoothstep the whole blend
  const a = ACTS[i];
  const b = ACTS[Math.min(i + 1, ACTS.length - 1)];

  return {
    indexA: i,
    indexB: Math.min(i + 1, ACTS.length - 1),
    morph: t,
    rawMorph: raw,
    camPos: lerpTriple(a.camera.pos, b.camera.pos, t, scratch.camPos),
    camLook: lerpTriple(a.camera.look, b.camera.look, t, scratch.camLook),
    fov: lerp(a.camera.fov, b.camera.fov, t),

    attract: lerp(a.sim.attract, b.sim.attract, t),
    damp: lerp(a.sim.damp, b.sim.damp, t),
    turb: lerp(a.sim.turb, b.sim.turb, t),
    noiseScale: lerp(a.sim.noiseScale, b.sim.noiseScale, t),
    swirl: lerp(a.sim.swirl, b.sim.swirl, t),

    size: lerp(a.look.size, b.look.size, t),
    intensity: lerp(a.look.intensity, b.look.intensity, t),
    iridescence: lerp(a.look.iridescence, b.look.iridescence, t),
    cold: lerpTriple(a.look.cold, b.look.cold, t, scratch.cold),
    warm: lerpTriple(a.look.warm, b.look.warm, t, scratch.warm),
    hot: lerpTriple(a.look.hot, b.look.hot, t, scratch.hot),

    skyTop: lerpTriple(a.sky.top, b.sky.top, t, scratch.skyTop),
    skyBottom: lerpTriple(a.sky.bottom, b.sky.bottom, t, scratch.skyBottom),
    nebula: lerpTriple(a.sky.nebulaColor, b.sky.nebulaColor, t, scratch.nebula),
    nebulaAmount: lerp(a.sky.nebulaAmount, b.sky.nebulaAmount, t),
    stars: lerp(a.sky.stars, b.sky.stars, t),
    oil: lerp(a.sky.oil, b.sky.oil, t),

    bloom: lerp(a.grade.bloom, b.grade.bloom, t),
    aberration: lerp(a.grade.aberration, b.grade.aberration, t),
    exposure: lerp(a.grade.exposure, b.grade.exposure, t),
    trail: lerp(a.grade.trail, b.grade.trail, t),
    vignette: lerp(a.grade.vignette, b.grade.vignette, t),
  };
}
